// The Feast (docs/04 §17.5, docs/07 §2): a banquet table laid over the grid. Only empty plates
// spin; a morsel that lands stays and the respins reset. The sim serves; the view sets the table.
import { Container, Graphics, Sprite } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { audio } from '../../audio/audio';
import { formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { feastValue } from '../../sim/bonus/feast';
import type { FeastBonus, Morsel } from '../../sim/types';
import { dispatch, getGame } from '../../state/store';
import { LOGICAL_W } from '../app';
import type { Fx } from '../fx/fx';
import type { Popups } from '../fx/Popups';
import { PixelButton } from '../machine/PixelButton';
import { clock } from '../timeline/clock';

const W = 300;
const CELL = 36;
const GX = 16;
const GY = 22;

const SPRITE: Record<Morsel['kind'], string> = {
  morsel: 'sym_morsel',
  fork: 'sym_fork',
  pepper: 'sym_pepper',
  appetizer: 'sym_appetizer',
  main: 'sym_main',
  dessert: 'sym_dessert',
  plug: 'sym_plug_part_5',
};

const feast = (): FeastBonus | null => {
  const b = getGame().run?.bonus;
  return b?.mode === 'feast' ? b : null;
};

export class FeastView extends Container {
  private panel = new Container();
  private bg = new Graphics();
  private title = new Sprite(textTexture(t('ui.feast.title'), 'gold', 'ink'));
  private table = new Graphics();
  private plates = new Container();
  private respins = new Sprite();
  private total = new Sprite();
  private serve = new PixelButton(76, 15, t('ui.feast.spin'));
  private shown: (string | null)[] = [];
  private busy = false;

  constructor(
    private popups: Popups,
    private fx: Fx,
  ) {
    super();
    this.visible = false;
    this.addChild(this.panel);
    this.title.position.set(Math.round((W - this.title.width) / 2), 3);
    this.respins.position.set(W - 90, 30);
    this.total.position.set(W - 90, 44);
    this.serve.position.set(W - 90, 70);
    this.serve.onClick = () => this.serveNow();
    this.panel.addChild(this.bg, this.title, this.table, this.plates, this.respins, this.total, this.serve);
  }

  serveNow(): void {
    const b = feast();
    if (!b || b.done || this.busy) return;
    audio.play('click');
    dispatch({ t: 'BONUS_INPUT', input: { kind: 'feast_spin' } });
  }

  /** The panel grows with the machine: 5×3 up to the Cathedral's 5×5 table. */
  private get panelH(): number {
    const b = feast();
    return GY + (b?.rows ?? 3) * CELL + 12;
  }

  private drawTable(b: FeastBonus): void {
    const H = this.panelH;
    this.bg.clear();
    this.bg.rect(-2, -2, W + 4, H + 4).fill(PALETTE.ink);
    this.bg.rect(0, 0, W, H).fill(PALETTE.wine);
    this.bg.rect(0, 0, W, 14).fill(PALETTE.maroon);
    const g = this.table.clear();
    g.rect(GX - 4, GY - 4, b.reels * CELL + 8, b.rows * CELL + 8).fill(PALETTE.cream);
    g.rect(GX - 4, GY - 4, b.reels * CELL + 8, b.rows * CELL + 8).stroke({ color: PALETTE.ink, width: 1 });
    for (let i = 0; i < b.reels * b.rows; i++) {
      const x = GX + (i % b.reels) * CELL;
      const y = GY + Math.floor(i / b.reels) * CELL;
      g.circle(x + CELL / 2, y + CELL / 2, 16).fill(PALETTE.white);
      g.circle(x + CELL / 2, y + CELL / 2, 16).stroke({ color: PALETTE.lilacGrey, width: 1 });
    }
  }

  private cellSprite(b: FeastBonus, i: number, m: Morsel): Container {
    const c = new Container();
    const s = new Sprite(getAtlas().tex(SPRITE[m.kind]));
    c.addChild(s);
    if (m.kind !== 'plug' && m.kind !== 'fork' && m.kind !== 'pepper') {
      const v = new Sprite(textTexture(String(m.value), 'gold', 'ink'));
      v.position.set(CELL - 4 - v.width, CELL - 12);
      c.addChild(v);
    }
    c.position.set(GX + (i % b.reels) * CELL + 2, GY + Math.floor(i / b.reels) * CELL + 2);
    return c;
  }

  private setPlates(b: FeastBonus): void {
    this.plates.removeChildren().forEach((c) => c.destroy());
    b.cells.forEach((m, i) => m && this.plates.addChild(this.cellSprite(b, i, m)));
    this.shown = b.cells.map((m) => (m ? m.kind : null));
  }

  private refresh(b: FeastBonus): void {
    const notation = getGame().meta.settings.notation;
    this.respins.texture = textTexture(t('ui.feast.respins', { n: b.respins }), b.respins <= 1 ? 'red' : 'paper', 'ink');
    this.total.texture = textTexture(t('ui.feast.total', { n: formatNumber(feastValue(b), notation) }), 'gold', 'ink');
    this.serve.set(!b.done && !this.busy);
  }

  open(): void {
    const b = feast();
    if (!b) return;
    this.busy = false;
    this.drawTable(b);
    this.setPlates(b);
    this.refresh(b);
    this.panel.position.set(Math.round(LOGICAL_W / 2 - W / 2 - 20), 370);
    this.visible = true;
    void clock.tween(this.panel, 350, { y: b.rows > 4 ? 60 : 76, ease: 'back.out(1.4)' });
  }

  /** A serve resolved: new plates drop onto the table with a clink. */
  async update(): Promise<void> {
    const b = feast();
    if (!b) return;
    this.busy = true;
    this.refresh(b);
    const fresh = b.cells.map((m, i) => (m && this.shown[i] !== m.kind ? i : -1)).filter((i) => i >= 0);
    if (!fresh.length) audio.play('nearMiss', { volume: 0.5 });
    for (const i of fresh) {
      const m = b.cells[i]!;
      const s = this.cellSprite(b, i, m);
      const y = s.y;
      s.y -= 20;
      s.alpha = 0;
      this.plates.addChild(s);
      audio.play(m.kind === 'plug' ? 'choir' : m.kind === 'morsel' ? 'coin' : 'sparkle', { pitch: fresh.indexOf(i) });
      await clock.tween(s, 120, { y, alpha: 1, ease: 'back.out(2)' });
      if (m.kind === 'fork' || m.kind === 'pepper') {
        void this.popups.float(t(`ui.feast.${m.kind}`), this.panel.x + s.x + 16, this.panel.y + s.y, 'mint', 1, 600);
      }
    }
    this.shown = b.cells.map((m) => (m ? m.kind : null));
    if (b.full) {
      audio.play('choir');
      this.fx.shake(4, 400);
      this.fx.flash('gold', 0.3, 200);
      await this.popups.banner(t('ui.feast.full'), this.panel.x + W / 2, this.panel.y + this.panelH / 2, 'gold', 3, 900);
    }
    this.busy = false;
    this.refresh(b);
  }

  async close(payout: string, voided: boolean): Promise<void> {
    const mid = { x: this.panel.x + W / 2, y: this.panel.y + this.panelH / 2 };
    if (!voided) {
      audio.play('scratchWin');
      await this.popups.banner(t('ui.scratch.win', { coins: formatNumber(payout) }), mid.x, mid.y, 'gold', 2, 700);
    }
    await clock.tween(this.panel, 300, { y: 390, ease: 'back.in(1.4)' });
    this.visible = false;
  }
}
